<script>
	import Heading from '$lib/components/Heading.svelte'
	import Faq from '$lib/components/Faq.svelte'

	import DomainLookup from '$lib/svg/mermaid_domain-lookup.svelte'
</script>

<article class="container mx-auto pt-6 md:pt-8 px-2 sm:px-4">
	<div class="prose mb-[3rem]">
		<Heading level={1}>Frequently asked questions</Heading>
		<p>While this concept is still new, we expect that some edge cases will be encountered. Here are some frequently asked questions we have internally.</p>
	</div>
	<Faq id="upload">
		<span slot="question">Where should I store a carbon.txt file for my organisation?</span>
		<div class="prose" slot="answer">
			<p>
				You should upload your carbon.txt file to a location on your website that is easily discoverable for both humans and machines. We recommend placing it in the root directory of your website, or
				in a well-known location such as <code>/.well-known/</code>.
			</p>

			<div class="prose mb-[2rem] ml-[1rem]">
				<Heading level={3}>Root of your domain</Heading>
				<p>
					Upload the carbon.txt file to the root of your domain - that is the URL should look something like this:
					<code>https://www.my-org.com/carbon.txt</code>.
				</p>
			</div>
			<div class="prose mb-[2rem] ml-[1rem]">
				<Heading level={3}>.well-known</Heading>
				<p>
					Upload the carbon.txt file to a <code>/.well-known/</code> path on your server. This follows existing conventions that web administrators will be familiar with.
				</p>
				<Heading level={3}>Anywhere else (using a DNS TXT record)</Heading>
				<p>
					Upload the carbon.txt file at any publicly accessible HTTP URL, then set up a DNS TXT record in the format <code>carbon-txt-location=URL</code> pointing to the location of this file.
				</p>
				<Heading level={3}>Anywhere else (using an HTTP header)</Heading>
				<p>
					Upload the carbon.txt file at any publicly accessible HTTP URL, then set up your web server to add the header <code>CarbonTxt-Location: URL</code> to all requests, pointing to the location of
					this file.
				</p>
			</div>
		</div>
	</Faq>
	<Faq id="why">
		<span slot="question">Why this approach?</span>
		<div class="prose" slot="answer">
			<p>There are a few reasons for taking the approach we’ve described on this site.</p>

			<p>
				Primarily, we are adopting an approach which leans heavily on existing web standards, and technologies. This allows for familiarity, and we hope will lead to these ideas being easier to
				adopt/implement.
			</p>

			<p>
				We believe that providing a simple way for organisations to implement the carbon.txt specification is key to broader adoption. It allows us, as a small not-for-profit driving this idea, to
				have a much larger reach &amp; impact when compared to the alternative of relying on individual action. Building this project around a plugin ecosystem also allows for other organisations to build
				on top of the carbon.txt specification, and extend it in ways that we haven't thought of.
			</p>

			<p>
				In the long run, we think that demonstrating how we can use existing internet and web standards to make sustainability claims easily discoverable, as well as human and machine readable, will
				result in a web where it's easier to trust green claims. Not only that, conventions like carbon.txt allows us follow green claims to the supporting evidence used to back them up.
			</p>
		</div>
	</Faq>
	<Faq id="ownership">
		<span slot="question">What would stop me using someone else's carbon.txt file instead?</span>
		<div class="prose" slot="answer">
			<p>
				Carbon.txt files are publically accessible, and are designed to be easily discoverable. This means that anyone can access them, and use the information contained within them. It allows for
				transparency, and for anyone to verify the claims made by an organisation.
			</p>
		</div>
	</Faq>
	<Faq id="validator-how">
		<span slot="question">How does the carbon.txt validator work?</span>
		<div class="prose" slot="answer">
			<p>There are three ways to use the carbon.txt validator:</p>

			<details class="mb-4 ml-4">
				<summary class="text-xl">Check a website domain</summary>

				<p>
					When you enter a website domain, the validator will check for a DNS TXT record in the format <code>carbon-txt-location=URL</code> and follow it, if it exists.
				</p>
				<p>
					If no DNS TXT record is found, it will proceed to look for a carbon.txt file in the root of the website (e.g. <code>https://example.com/carbon.txt</code>). If it finds one, it will parse the
					file and return the information contained within it.
				</p>
				<p>
					If there is no carbon.txt file at the root of the domain, it will then check for the file in a well-known location on the server (e.g. <code>https://example.com/.well-known/carbon.txt</code
					>). Again, if it finds one, it will parse the file and return the information contained within it.
				</p>
				<p>
					If no file is found at either location, then the validator will proceeed to check for a <code>CarbonTxt-Location</code> HTTP header specifying a URL, and follow it.
				</p>
				<p>
					If none of the above succeed, and you have requested either a top-level domain, or the www. subdomain of a top level domain - the validator will then attempt to find the carbontxt at the
					other alternative automatically (the www. subdomain will be tried, if a TLD was requested, the TLD will be tried if a www. subdomain is requested). The entire process is repeated for the
					alternate domain. For any other subdomains, no further attempts are carried out.
				</p>
				<p>If none of the above are found, the validator will return an error message.</p>

				<p>The diagram below shows this process visually.</p>

				<DomainLookup />
			</details>

			<details class="mb-4 ml-4">
				<summary class="text-xl">Check a carbon.txt URL</summary>

				<p>
					When you enter a URL that points to a specific carbon.txt file (e.g. <code>https://example.com/carbon.txt</code>), the validator will look make a request to find the file at that location.
					If it finds one, it will parse the file and return the information contained within it. If no file is found, then the validator will return an error message.
				</p>
			</details>

			<details class="mb-4 ml-4">
				<summary class="text-xl">Manually enter the content of a carbon.txt file</summary>

				<p>
					If you manually enter the content of a carbon.txt file, then the validator will try to parse the syntax that has been entered and return the information contained within it. If the syntax is
					incorrect, then the validator will return an error message.
				</p>
			</details>
		</div>
	</Faq>
	<Faq id="document-types">
		<span slot="question">What types of document can I link to as evidence in my carbon.txt file?</span>
		<div class="prose" slot="answer">
      <p>We accept a wide variety of document types as evidence in the <strong>organisatioal disclosures</strong> section of your carbon.txt file. Currently, these include:</p>
      <dl>
        <dt>
          Sustainability page
        </dt>
        <dd>
          A link to the sustainability policy page of your website, or any other page which sets out your organisation's environmental or sustainability policies and commitments.
        </dd>
        <dt>
          Annual Report
        </dt>
        <dd>
          Official annual report for your company, including information on sustainability or CSR comitments, carbon reduction activity, or progress towards sustainability goals.
        </dd>
        <dt>
          Certificate
        </dt>
        <dd>
          A certificate provided by an external entity to your organization - these could be Guarantees of Origin or RECS (Renweable Energy Certificates) for your electricity usage, or certificates of compliance to an external <a href="#certification-schemes">certification scheme</a> such as <a href="https://www.bcorporation.net/en-us/certification/" target="_blank">B-corp</a> or <a href="https://tcocertified.com/" target="_blank">TCO Certified</a>.
        </dd>
        <dt>
          CSRD Report
        </dt>
        <dd>
          A report produced for compliance with the EU <a href="https://finance.ec.europa.eu/financial-markets/company-reporting-and-auditing/company-reporting/corporate-sustainability-reporting_en" target="_blank">Corporate Sustainability Reporting Directive</a>.
        </dd>
        <dt>
          AI Model Card
        </dt>
        <dd>
          If you use AI models as part of your operations, a link to an <a href="https://huggingface.co/docs/hub/model-cards" target="_blank">AI model card</a> in markdown format, containing carbon footprint information.
        </dd>
        <dt>
          Measurement data
        </dt>
        <dd>
          Any quantitative information, measured or estimated, about your organization's carbon emissions or other environmental impacts, in a structured, machine-readable format such as the <a href="https://dist.greenweb.org/schema" target="_blank">DIST schema</a>.
        </dd>
        <dt>
          Other
        </dt>
        <dd>
          Any other document which provides evidence of your carbon footprint, sustainability policy, or related commitments. If you want to recommend any new document types for inclusion in future versions of carbon.txt, you can <a href="https://github.com/thegreenwebfoundation/carbon.txt/issues" target="_blank"> create an issue in our git repository</a>.
        </dd>
        <dt>
          Web page (deprecated)
        </dt>
        <dd>
          Currently, carbon.txt will also accept the value of "web-page" as a document type for any pertinent web-page. However, use of this is discouraged (in favour of "Sustainabilty page" or "Other") and this document type will be formally deprecated in a future release.
        </dd>
      </dl>
		</div>
	</Faq>
	<Faq id="certification-schemes">
		<span slot="question">What is a certification scheme, and how can I include one in my carbon.txt file?</span>
		<div class="prose" slot="answer">
      <p>In carbon.txt, we use the term <strong>certification schemes</strong> to refer to any independent third party which both <strong>sets out voluntary standards on sustainability, energy use, or emissions commitments</strong>, and also <strong>audits or verifies an organization's adherence to them</strong>. A good example is something like <a href="https://www.bcorporation.net" target="_blank">B-corp</a>, which both sets out the <a href="https://www.bcorporation.net/en-us/standards/performance-requirements/" target="_blank">B Lab Standards for social environmental and governance practices</a>, and <a href="https://www.bcorporation.net/en-us/certification/">certifies companies for compliance with them</a>.</p>
      <p>Certification schemes are important in carbon.txt for two reasons: Firstly, by surfacing an organization's certifications, users of carbon.txt can have increased trust in their disclosures (as they have been indepently certified), and can better understand their specific sustainability commitments and policies, rather than just the actions that arise from them. In addition, certifying organizations themselves can use carbon.txt to more easily access the evidence they need to certify an organization.</p>
      <p>
        In carbon.txt, certification schemes are specified explicitly in a dedicated block of the file, and then referred to by disclosures that are relevant to them. This allows users to quickly identify which disclosures provide evidence for which particular certification schemes. For further details, <a href="https://www.thegreenwebfoundation.org/news/carbon-txt-v0-6-certification-schemes-measurement-data-and-more/" target="_blank">please see our blog post</a>.
      </p>
		</div>
	</Faq>
	<Faq id="developer-tools">
		<span slot="question">I'm a developer, how can I use carbon.txt in my own applications?</span>
		<div class="prose" slot="answer">
      <p>We offer two ways of extending and using carbon.txt in your own software - a python package, and an HTTP API.</p>
      <details class="mb-4 ml-4">
        <summary class="text-xl">The carbon.txt python package</summary>
        <p>Our <a href="https://pypi.org/project/carbon-txt/" target="_blank">carbon.txt python package</a> is the reference implementation of the carbon.txt syntax, and powers the validator on this site as well as carbon.support in the <a href="https://www.thegreenwebfoundation.org/green-web-check/" target="_blank">Green Web Check tool</a> - it also allows you to build and validate carbon.txt files programatically within your own applications.</p>
        <p>There is <a href="https://carbon-txt-validator.readthedocs.io/en/latest/" target="_blank">documentation on the python package available at ReadTheDocs</a>, and <a href="https://github.com/thegreenwebfoundation/carbon-txt-validator" target="_blank">source code on GitHub</a>.</p>
        <p>More <a href="https://developers.thegreenwebfoundation.org/carbon-txt/overview/" target="_blank">general technical documentation on the carbon.txt project</a> is available on our developer documentation site.</p>
      </details>
      <details class="mb-4 ml-4">
        <summary class="text-xl">The carbon.txt validation API</summary>
        <p>If you're not working with python, or if you prefer not to rely on our package for some other reason, we offer an HTTP API for performing carbon.txt lookups and validating carbon.txt files. This API allows you to check the syntax and contents of a carbon.txt file, provided as a URL or directly as text, as well as look up the carbon.txt file for any domain.</p>
        <p>API documentation is available at <a href="https://carbon-txt-api.greenweb.org/api/docs" target="_blank">carbon-txt-api.greenweb.org/api/docs</a>, and further information and usage examples on our <a href="https://developers.thegreenwebfoundation.org/api/carbon-txt/overview/" target="_blank">developer documentation site</a>. To access this API you will need to register for an API key on the <a href="https://admin.thegreenwebfoundation.org" target="_blank">Green Web portal</a>, and pass it in the <code>X-API-Key</code> header of your API requests. By default, requests are rate-limited to 2 per second per key - if you need a higher limit, please <a href="mailto:support@greenweb.org?subject=Raise%20API%20key%20request%20limit">get in touch</a> to discuss your use case!</p>
        <p>If you're working in Javascript, our <a href="https://www.npmjs.com/package/@tgwf/co2" target="_blank">CO2.js</a> library provides a useful wrapper client for this API - full details are available in the <a href="https://developers.thegreenwebfoundation.org/co2js/overview/" target="_blank">developer documentation</a>.</p>
      </details>
		</div>
	</Faq>
</article>
